<template>
    <div>
        <rkm-data-table
            :fields="fields"
            :repository="repositories.paciente"
            :request-params="requestParams"
            :disabled-button-search="true"
            :tbody-tr-class="rowClass"
            class="tabela-paciente"
            ref="DataTable"
            search-by="nome"
        >
            <template v-slot:cell(nome)="{ item }">
                {{ item.nome }}

                <span
                    v-if="item.obitoEm"
                    class="ml-1"
                    title="Paciente em óbito"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="cross" />
                </span>

                <span
                    v-if="item.bloqueado"
                    class="ml-1"
                    title="Cadastro bloqueado"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="ban" />
                </span>

                <span
                    v-if="isProvisorioOuTemporario(item)"
                    class="ml-1"
                    title="Paciente provisório/temporário"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="hourglass-half" />
                </span>

                <span
                    v-if="item.classificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                    class="ml-1"
                    title="Paciente não munícipe"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="map-pin" />
                </span>

                <span
                    v-if="!item.cpf && !item.cns"
                    class="ml-1"
                    title="Paciente sem CPF e CNS"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="exclamation-triangle" />
                </span>

                <span
                    v-if="item.pacienteResponsavelFamiliar"
                    class="ml-1"
                    title="Responsável familiar"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="crown" />
                </span>

                <span
                    v-if="isIdoso(item.dataNascimento)"
                    class="ml-1"
                    title="Idoso"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="blind" />
                </span>

                <span
                    v-if="hasTea(item)"
                    class="ml-1"
                    title="Pessoa com Transtorno do Espectro Autista - TEA"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="infinity" />
                </span>

                <span
                    v-if="hasPcd(item)"
                    class="ml-1"
                    title="Pessoa com Deficiência - PCD"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="wheelchair" />
                </span>
            </template>

            <template v-slot:cell(actions)="{ item }">
                <slot
                    name="actions"
                    v-bind:item="item"
                >
                </slot>
            </template>

            <template v-slot:cell(dataNascimento)="row">
                {{ row.value | formatDate }}
            </template>

            <template v-slot:cell(cpf)="row">
                {{ row.value | formatCpf }}
            </template>

            <template v-slot:cell(cns)="row">
                {{ row.value | formatCns }}
            </template>
        </rkm-data-table>

        <rkm-row class="mt-3">
            <rkm-col>
                <p class="m-0">
                    <span class="legend-color purple" /> Paciente em óbito<br>
                    <span class="legend-color danger" /> Cadastro bloqueado<br>
                    <span class="legend-color orange" /> Paciente provisório ou temporário<br>
                    <span class="legend-color brown" /> Paciente não munícipe<br>
                    <span class="legend-color warning" /> Paciente sem CPF e CNS
                </p>
            </rkm-col>
        </rkm-row>
    </div>
</template>

<script>
import { repository, error } from '@/mixins'
import formats from '@/filters/formats'
import { mapActions } from 'vuex'
import moment from 'moment'

// Enums.
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

export default {
    created() {
        this.repositories = {
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
        }
    },
    data: () => ({
        enums: { classificacao, subclassificacao, deficienciaBase, deficiencia },
    }),
    computed: {
        requestParams() {

            return { with_paciente_bloqueado: true }
        },
        fields() {
            return [
                {
                    key: 'prontuario',
                    name: 'cadastro_paciente.prontuario',
                    label: 'Prontuário',
                    searchable: true,
                    sortable: true
                },
                {
                    key: 'nome',
                    name: 'cadastro_cidadao.nome',
                    label: 'Nome',
                    searchable: true,
                    sortable: true
                },
                {
                    key: 'nomeMae',
                    name: 'cadastro_cidadao.nome_mae',
                    label: 'Nome da Mãe',
                    searchable: true,
                    sortable: true
                },
                {
                    key: 'dataNascimento',
                    name: 'cadastro_cidadao.data_nascimento',
                    label: 'Data de nascimento',
                    searchable: true,
                    sortable: true
                },
                {
                    key: 'cns',
                    name: 'cadastro_cidadao.cns',
                    label: 'CNS',
                    searchable: true,
                    sortable: true
                },
                {
                    key: 'cpf',
                    name: 'cadastro_cidadao.cpf',
                    label: 'CPF',
                    searchable: true,
                    sortable: true
                },
                {
                    key: 'actions',
                    label: 'Ações',
                    class: 'col-min'
                },
            ]
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        isProvisorioOuTemporario(item) {

            return item.classificacaoId == this.enums.classificacao.PROVISORIO
                || item.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        isIdoso(dataNascimento) {

            return !!dataNascimento && moment().diff(moment(dataNascimento), 'years') >= 60
        },
        getDeficienciasBaseIds(item) {

            return item.deficienciasBaseIds
                ? item.deficienciasBaseIds.split(',').map(Number)
                : []
        },
        hasTea(item) {

            return this.getDeficienciasBaseIds(item).includes(this.enums.deficienciaBase.TEA)
                || item.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        hasPcd(item) {

            return this.getDeficienciasBaseIds(item).some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!item.deficienciaId && item.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        rowClass(item, type) {
            if(item && type === 'row') {
                if (item.obitoEm) {
                    return 'table-purple'
                }
                if (item.bloqueado) {
                    return 'table-danger'
                }
                if (this.isProvisorioOuTemporario(item)) {
                    return 'table-orange'
                }
                if (item.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                    return 'table-brown'
                }
                if (!item.cpf && !item.cns) {
                    return 'table-warning'
                }
            }
        },
    },
    filters: {
        formatDate: formats.formatDate,
        formatCns: formats.formatCns,
        formatCpf: formats.formatCpf,
    },
    mixins: [ repository, error ]
}
</script>

<style scoped>
.legend-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 4px;
}
.legend-color.purple {
    background-color: #6f42c1;
}
.legend-color.danger {
    background-color: var(--danger);
}
.legend-color.orange {
    background-color: #F28C28;
}
.legend-color.brown {
    background-color: #6F4E37;
}
.legend-color.warning {
    background-color: var(--warning);
}

.tabela-paciente >>> .table-orange {
    border-color: #F28C28;
    background-color: #FFE0B2;
}

.tabela-paciente >>> .table-brown {
    border-color: #6F4E37;
    background-color: #D2B48C;
}

.tabela-paciente >>> .table-purple {
    border-color: #6f42c1;
    background-color: #E1D4F2;
}
</style>
